import React, { useState, useEffect } from 'react';
import { City, Country } from 'country-state-city';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    TextField,
    Grid,
    Stack,
    Chip,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    OutlinedInput,
    Snackbar,
    Alert,
    Autocomplete,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    useMediaQuery,
    useTheme,
    InputAdornment
} from '@mui/material';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EventIcon from '@mui/icons-material/Event';
import MicIcon from '@mui/icons-material/Mic';

import { useContactUpdate } from '../../hooks/useContactUpdate';
import AddressAutocompleteField, { getCityCoordinates, majorCities } from '../Contacts/AddressAutocompleteField';
import LocationPickerDialog from '../Maps/LocationPickerDialog';
import AddImportantDateDialog from '../Contacts/AddImportantDateDialog';
import CompactVoiceRecorder from '../Contacts/CompactVoiceRecorder';
import RelationshipRhythmSlider from '../Contacts/RelationshipRhythmSlider';

/**
 * Reusable component for editing contact information
 */
const EditContactDialog = ({
    open,
    contact,
    onClose,
    onSave,
    t = {}
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { updateContactFull, normalizeContactData } = useContactUpdate();
    const [editedContact, setEditedContact] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'info'
    });
    const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);
    const [isImportantDateDialogOpen, setIsImportantDateDialogOpen] = useState(false);

    // Initialize form with contact data when the dialog opens
    useEffect(() => {
        // Clear any existing focus when dialog opens
        if (open) {
            setTimeout(() => {
                document.querySelectorAll(':focus').forEach(el => {
                    if (el.blur) el.blur();
                });
            }, 100);
        }
        
        if (contact && open) {
            // Проверяем наличие ID контакта
            if (!contact._id) {
                console.error('Contact is missing ID:', contact);
                setError('Cannot edit contact: Missing contact ID');
                setSnackbar({
                    open: true,
                    message: 'Cannot edit contact: Missing contact ID',
                    severity: 'error'
                });
                return;
            }
            
            try {
                // Make a deep copy of the contact data
                const contactCopy = JSON.parse(JSON.stringify(contact));
                
                // Убедимся, что ID сохраняется
                if (!contactCopy._id && contact._id) {
                    contactCopy._id = contact._id;
                }
                
                // Ensure these fields exist even if they're not in the original contact
                contactCopy.socialMedia = contactCopy.socialMedia || {};
                contactCopy.tags = contactCopy.tags || [];
                contactCopy.importantDates = contactCopy.importantDates || [];
                contactCopy.structuredCountry = contactCopy.structuredCountry || '';
                                contactCopy.structuredCity = contactCopy.structuredCity || '';
                                contactCopy.isConnector = contactCopy.isConnector || false;
                                contactCopy.isBrain = contactCopy.isBrain || false;
                                contactCopy.notes = contactCopy.notes || '';
                                contactCopy.contactFrequency = contactCopy.contactFrequency || 30;
                
                // Обработка поля name для отображения в форме
                if (contactCopy.name && !contactCopy.fullName) {
                    contactCopy.fullName = contactCopy.name;
                }
                // Сохраняем оригинальное имя для отслеживания изменений
                contactCopy.originalName = contactCopy.name;
                
                // Используем старые поля country и city, если структурированные поля пусты
                if (!contactCopy.structuredCountry && contactCopy.country) {
                    contactCopy.structuredCountry = contactCopy.country;
                }
                
                if (!contactCopy.structuredCity && contactCopy.city) {
                    contactCopy.structuredCity = contactCopy.city;
                }
                
                // Initialize countryData for the autocomplete field if structuredCountry exists
                if (contactCopy.structuredCountry) {
                    
                    // Try to find the country in the library by name
                    const allCountries = Country.getAllCountries();
                    
                    const foundCountry = allCountries.find(
                        c => c.name.toLowerCase() === contactCopy.structuredCountry.toLowerCase()
                    );
                    
                    if (foundCountry) {
                        contactCopy.countryData = {
                            name: foundCountry.name,
                            code: foundCountry.isoCode,
                            flag: foundCountry.flag,
                            phoneCode: foundCountry.phonecode
                        };
                    } else {
                        // Если страна не найдена в библиотеке, создаем базовый объект
                        contactCopy.countryData = { 
                            name: contactCopy.structuredCountry
                            // Код страны останется undefined
                        };
                    }
                } else {
                }
                
                setEditedContact(contactCopy);
            } catch (error) {
                console.error('Error processing contact data:', error);
                setError('Error loading contact data: ' + error.message);
            }
        } else if (!contact) {
            setEditedContact({});
        }
    }, [contact, open]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        
        // ИСПРАВЛЕНИЕ: Используем функциональное обновление состояния
        setEditedContact(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Обработчик для полей автозаполнения адреса
    // Обработчик для выбора местоположения через MapLibre
    const handleLocationSelect = (location) => {
        console.log('Location selected:', location);
        
        setEditedContact(prev => ({
            ...prev,
            structuredCity: location.structuredCity || '',
            structuredCountry: location.structuredCountry || '',
            coordinates: location.coordinates || null
        }));
        
        setIsLocationPickerOpen(false);
    };
    
    // Старый обработчик для обратной совместимости
    const handleAddressAutocompleteChange = (field, value) => {
        console.log(`Address field ${field} changed:`, value);
        
        if (field === 'country') {
            setEditedContact(prev => ({
                ...prev,
                structuredCountry: value ? value.name : '',
                countryData: value, // Сохраняем полный объект страны
                // Сбрасываем город при смене страны
                ...(prev.structuredCity ? { structuredCity: '', coordinates: null } : {})
            }));
        } else if (field === 'city') {
            // Обрабатываем как объекты, так и строковые значения
            const cityName = typeof value === 'string' ? value : (value ? value.name : '');
            
            // Используем библиотеку для получения координат города
            const findCityCoordinates = (cityName) => {
                if (!cityName) return null;
                
                const normalizedCityName = cityName.toLowerCase().trim();
                
                // Специальные случаи, которых может не быть в библиотеке
                if (normalizedCityName === 'dushanbe' || normalizedCityName === 'душанбе') {
                    return {
                        name: 'Dushanbe',
                        latitude: 38.5598,
                        longitude: 68.7870,
                        countryName: 'Tajikistan'
                    };
                }
                
                // Получаем все страны
                const countries = Country.getAllCountries();
                
                // Проходим по всем странам и ищем город
                for (const country of countries) {
                    const cities = City.getCitiesOfCountry(country.isoCode);
                    if (!cities) continue;
                    
                    const city = cities.find(c => c.name.toLowerCase() === normalizedCityName);
                    if (city && city.latitude && city.longitude) {
                        return {
                            name: city.name,
                            latitude: parseFloat(city.latitude),
                            longitude: parseFloat(city.longitude),
                            countryName: country.name
                        };
                    }
                }
                
                return null;
            };
            
            const cityCoords = findCityCoordinates(cityName);
            if (cityCoords) {
                console.log(`Found coordinates for city ${cityName}:`, cityCoords);
                setEditedContact(prev => ({
                    ...prev,
                    structuredCity: cityCoords.name,
                    structuredCountry: cityCoords.countryName || prev.structuredCountry,
                    coordinates: {
                        latitude: cityCoords.latitude,
                        longitude: cityCoords.longitude
                    }
                }));
                return;
            }
            
            const coordinates = value && value.coordinates ? {
                latitude: value.coordinates[0],
                longitude: value.coordinates[1]
            } : null;
            
            // Проверяем, есть ли город в списке majorCities
            if (cityName && !coordinates) {
                const normalizedCityName = cityName.toLowerCase()
                    .trim()
                    .replace(/saint/g, 'st')
                    .replace(/\./g, '')  // Remove periods
                    .replace(/,/g, '')   // Remove commas
                    .replace(/-/g, ' '); // Replace hyphens with spaces
                
                if (majorCities[normalizedCityName]) {
                    const coords = majorCities[normalizedCityName];
                    
                    // Обновляем состояние с найденными координатами
                    setEditedContact(prevState => ({
                        ...prevState,
                        structuredCity: cityName,
                        coordinates: {
                            latitude: coords[0],
                            longitude: coords[1]
                        }
                    }));
                    
                    // Показываем сообщение об успехе
                    setSnackbar({
                        open: true,
                        message: `Coordinates for ${cityName} found successfully`,
                        severity: 'success'
                    });
                    
                    return; // Exit function as coordinates are already updated
                }
            }
            
            // Обновляем состояние контакта с новыми данными
            setEditedContact(prevState => ({
                ...prevState,
                structuredCity: cityName,
                coordinates: coordinates
            }));
            
            // Если есть название города, но нет координат, попробуем их получить
            if (cityName && !coordinates && editedContact.countryData && editedContact.countryData.code) {
                (async () => {
                    try {
                        const countryCode = editedContact.countryData.code;
                        
                        const coords = await getCityCoordinates(cityName, countryCode);
                        if (coords) {
                            
                            // Обновляем состояние с найденными координатами
                            setEditedContact(prevState => ({
                                ...prevState,
                                coordinates: {
                                    latitude: coords[0],
                                    longitude: coords[1]
                                }
                            }));
                            
                            // Показываем сообщение об успехе
                            setSnackbar({
                                open: true,
                                message: `Coordinates for ${cityName} found successfully`,
                                severity: 'success'
                            });
                        } else {
                            console.warn(`No coordinates found for city: ${cityName}`);
                            
                            // Показываем предупреждение о отсутствии координат
                            setSnackbar({
                                open: true,
                                message: t.coordinatesNotFound || 'Coordinates not found, map display may be limited',
                                severity: 'warning'
                            });
                        }
                    } catch (error) {
                        console.error('Error getting coordinates for city:', error);
                        
                        // Показываем ошибку
                        setSnackbar({
                            open: true,
                            message: `Error getting coordinates: ${error.message}`,
                            severity: 'error'
                        });
                    }
                })();
            }
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Create a copy for saving to avoid sending temporary UI data
            const contactToSave = { ...editedContact };
            
            // Обновляем поле name из fullName если оно изменилось
            if (contactToSave.fullName && contactToSave.fullName !== contactToSave.originalName) {
                contactToSave.name = contactToSave.fullName;
                console.log('Updated contact name from:', contactToSave.originalName, 'to:', contactToSave.name);
            }
            
            // Проверяем, что у контакта есть ID
            if (!contactToSave._id) {
                console.error('Cannot save contact: Missing contact ID', contactToSave);
                setError('Cannot save contact: Missing contact ID');
                setSnackbar({
                    open: true,
                    message: 'Cannot save contact: Missing contact ID',
                    severity: 'error'
                });
                setLoading(false);
                return;
            }
            
            // Если координаты отсутствуют, но город указан, попробуем найти координаты в списке majorCities
            if (contactToSave.structuredCity && !contactToSave.coordinates) {
                const normalizedCityName = contactToSave.structuredCity.toLowerCase()
                    .trim()
                    .replace(/saint/g, 'st')
                    .replace(/\./g, '')  // Remove periods
                    .replace(/,/g, '')   // Remove commas
                    .replace(/-/g, ' '); // Replace hyphens with spaces
                
                if (majorCities[normalizedCityName]) {
                    const coords = majorCities[normalizedCityName];
                    
                    contactToSave.coordinates = {
                        latitude: coords[0],
                        longitude: coords[1]
                    };
                    
                }
            }
            
            // Создаем или обновляем массив addresses, если есть информация о городе или стране
            if (contactToSave.structuredCountry || contactToSave.structuredCity) {
                
                // Создаем объект адреса
                const addressObj = {
                    structuredCountry: contactToSave.structuredCountry || '',
                    structuredCity: contactToSave.structuredCity || '',
                    address: contactToSave.address || '',
                    isPrimary: true
                };
                
                // Добавляем координаты, если они есть
                if (contactToSave.coordinates) {
                    addressObj.coordinates = contactToSave.coordinates;
                } else if (contactToSave.structuredCity) {
                    // Если координаты отсутствуют, но город указан, попробуем найти координаты
                    
                    // Если есть код страны, попробуем найти координаты для города
                    if (contactToSave.countryData && contactToSave.countryData.code) {
                        try {
                            const coords = await getCityCoordinates(contactToSave.structuredCity, contactToSave.countryData.code);
                            if (coords) {
                                addressObj.coordinates = {
                                    latitude: coords[0],
                                    longitude: coords[1]
                                };
                                
                                // Обновляем координаты в основном объекте контакта
                                contactToSave.coordinates = addressObj.coordinates;
                            }
                        } catch (error) {
                            console.error('Error getting coordinates for city:', error);
                        }
                    }
                }
                
                // Создаем или обновляем массив addresses
                if (!contactToSave.addresses || !Array.isArray(contactToSave.addresses)) {
                    contactToSave.addresses = [addressObj];
                } else {
                    // Проверяем, есть ли уже адрес с такими же данными
                    const existingAddressIndex = contactToSave.addresses.findIndex(
                        addr => addr.structuredCountry === addressObj.structuredCountry && 
                               addr.structuredCity === addressObj.structuredCity
                    );
                    
                    if (existingAddressIndex >= 0) {
                        // Обновляем существующий адрес
                        contactToSave.addresses[existingAddressIndex] = {
                            ...contactToSave.addresses[existingAddressIndex],
                            ...addressObj
                        };
                    } else {
                        // Добавляем новый адрес
                        contactToSave.addresses.push(addressObj);
                    }
                }
                
                
            // Убедимся, что поля structuredCountry и structuredCity в корне объекта контакта также обновлены
            // Это нужно, потому что на сервере нет поля addresses в модели контакта
            if (contactToSave.addresses && contactToSave.addresses.length > 0) {
                const primaryAddress = contactToSave.addresses.find(addr => addr.isPrimary) || contactToSave.addresses[0];
                contactToSave.structuredCountry = primaryAddress.structuredCountry;
                contactToSave.structuredCity = primaryAddress.structuredCity;
                contactToSave.address = primaryAddress.address;
                
                if (primaryAddress.coordinates) {
                    contactToSave.coordinates = primaryAddress.coordinates;
                }
            }
            
            // Проверяем координаты города через библиотеку
            if (contactToSave.structuredCity && !contactToSave.coordinates) {
                const findCityCoordinates = (cityName) => {
                    if (!cityName) return null;
                    
                    const normalizedCityName = cityName.toLowerCase().trim();
                    
                    // Специальные случаи
                    if (normalizedCityName === 'dushanbe' || normalizedCityName === 'душанбе') {
                        return {
                            name: 'Dushanbe',
                            latitude: 38.5598,
                            longitude: 68.7870,
                            countryName: 'Tajikistan'
                        };
                    }
                    
                    // Получаем все страны
                    const countries = Country.getAllCountries();
                    
                    // Проходим по всем странам и ищем город
                    for (const country of countries) {
                        const cities = City.getCitiesOfCountry(country.isoCode);
                        if (!cities) continue;
                        
                        const city = cities.find(c => c.name.toLowerCase() === normalizedCityName);
                        if (city && city.latitude && city.longitude) {
                            return {
                                name: city.name,
                                latitude: parseFloat(city.latitude),
                                longitude: parseFloat(city.longitude),
                                countryName: country.name
                            };
                        }
                    }
                    
                    return null;
                };
                
                const cityCoords = findCityCoordinates(contactToSave.structuredCity);
                if (cityCoords) {
                    console.log(`Found coordinates for city ${contactToSave.structuredCity}:`, cityCoords);
                    contactToSave.structuredCity = cityCoords.name;
                    if (!contactToSave.structuredCountry) {
                        contactToSave.structuredCountry = cityCoords.countryName;
                    }
                    contactToSave.coordinates = {
                        latitude: cityCoords.latitude,
                        longitude: cityCoords.longitude
                    };
                }
            }
            }
            
            // Сохраняем данные о стране и городе перед удалением временных объектов
            if (contactToSave.countryData) {
                // Сохраняем ISO код страны и другие данные перед удалением объекта
                if (contactToSave.countryData.code) {
                    contactToSave.countryCode = contactToSave.countryData.code;
                }
                if (contactToSave.countryData.flag) {
                    contactToSave.countryFlag = contactToSave.countryData.flag;
                }
                if (contactToSave.countryData.phonecode) {
                    contactToSave.countryPhoneCode = contactToSave.countryData.phonecode;
                }
            }
            
            // Очищаем проблемные поля перед отправкой
            console.log('🧹 Cleaning contact data before sending...');
            
            // Удаляем поля, которые могут вызывать проблемы с валидацией
            delete contactToSave.countryFlag;
            delete contactToSave.countryPhoneCode;
            delete contactToSave.countryData;
            delete contactToSave.originalName;
            delete contactToSave.fullName;
            
            // Очищаем только null и undefined значения (НЕ удаляем пустые строки)
            Object.keys(contactToSave).forEach(key => {
                if (contactToSave[key] === null || contactToSave[key] === undefined) {
                    // Сохраняем важные поля даже если они null/undefined
                    const importantFields = ['coordinates', 'notes', 'address', 'structuredCountry', 'structuredCity', 'isBrain', 'isConnector', 'contactFrequency'];
                    if (!importantFields.includes(key)) {
                        delete contactToSave[key];
                    } else {
                        // Для важных полей преобразуем null/undefined в соответствующие значения по умолчанию
                        if (key === 'notes' && (contactToSave[key] === null || contactToSave[key] === undefined)) {
                            contactToSave[key] = '';
                        }
                        if ((key === 'isBrain' || key === 'isConnector') && (contactToSave[key] === null || contactToSave[key] === undefined)) {
                            contactToSave[key] = false;
                        }
                        if (key === 'contactFrequency' && (contactToSave[key] === null || contactToSave[key] === undefined)) {
                            contactToSave[key] = 30;
                        }
                    }
                }
            });
            
            // Проверяем и очищаем поле relations
            if (contactToSave.relations) {
                contactToSave.relations = contactToSave.relations.filter(relation => 
                    relation && 
                    relation.contactId && 
                    relation.contactId !== 'undefined' &&
                    relation.contactName && 
                    relation.type
                );
            }
            
            // Проверяем socialMedia объект
            if (contactToSave.socialMedia) {
                Object.keys(contactToSave.socialMedia).forEach(key => {
                    if (!contactToSave.socialMedia[key] || contactToSave.socialMedia[key].trim() === '') {
                        delete contactToSave.socialMedia[key];
                    }
                });
                
                // Если socialMedia пустой, удаляем его
                if (Object.keys(contactToSave.socialMedia).length === 0) {
                    delete contactToSave.socialMedia;
                }
            }
            
            console.log('🔄 Contact data being saved:');
            console.log('- ID:', contactToSave._id);
            console.log('- Name:', contactToSave.name);
            console.log('- Country:', contactToSave.structuredCountry);
            console.log('- City:', contactToSave.structuredCity);
            console.log('- Country Code:', contactToSave.countryCode);
            console.log('- Coordinates:', contactToSave.coordinates);
            console.log('- Address:', contactToSave.address);
            console.log('- Tags:', contactToSave.tags);
            console.log('- Notes LENGTH:', contactToSave.notes ? contactToSave.notes.length : 'NULL');
            console.log('- Notes CONTENT:', JSON.stringify(contactToSave.notes));
            console.log('- isConnector:', contactToSave.isConnector);
            console.log('- isBrain:', contactToSave.isBrain);
            console.log('- contactFrequency:', contactToSave.contactFrequency);
            
            // Логируем исходные данные формы
            console.log('📝 BEFORE CLEANING - editedContact state:');
            console.log('- notes:', JSON.stringify(editedContact.notes));
            console.log('- isBrain:', editedContact.isBrain);
            console.log('- isConnector:', editedContact.isConnector);
            console.log('- contactFrequency:', editedContact.contactFrequency);
            
            console.log('📝 AFTER CLEANING - contactToSave data:');
            console.log('- notes:', JSON.stringify(contactToSave.notes));
            console.log('- isBrain:', contactToSave.isBrain);
            console.log('- isConnector:', contactToSave.isConnector);
            console.log('- contactFrequency:', contactToSave.contactFrequency);
            
            
            // Используем унифицированную функцию обновления
            const updatedContact = await updateContactFull(contactToSave._id, contactToSave);
            
            console.log('✅ Contact updated successfully:');
            console.log('- Returned Name:', updatedContact.name);
            console.log('- Returned Country:', updatedContact.structuredCountry);
            console.log('- Returned City:', updatedContact.structuredCity);
            console.log('- Returned Notes:', updatedContact.notes);
            
            // Вызываем callback родителя с обновленными данными
            if (onSave) {
                onSave(updatedContact);
            }
            
            // Show success message
            setSnackbar({
                open: true,
                message: t.contactSaved || 'Contact saved successfully',
                severity: 'success'
            });
            
            // Close dialog after saving with proper focus management
            setTimeout(() => {
                handleDialogClose();
            }, 1000);
        } catch (error) {
            console.error('Error saving contact:', error);
            setError(error.message || 'Error saving contact');
            
            // Show error message
            setSnackbar({
                open: true,
                message: error.message || 'Error saving contact',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };
    
    // For handling snackbar close
    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Handle important dates
    const handleAddImportantDate = (dateData) => {
        // If type is missing, set it to "Other" for backward compatibility
        const dateWithDefaults = {
            ...dateData,
            type: dateData.type || "Other"
        };
        
        setEditedContact(prev => ({
            ...prev,
            importantDates: [...(prev.importantDates || []), dateWithDefaults]
        }));
        
        setSnackbar({
            open: true,
            message: 'Important date added successfully',
            severity: 'success'
        });
    };
    
    const handleRemoveImportantDate = (index) => {
        setEditedContact(prev => ({
            ...prev,
            importantDates: prev.importantDates.filter((_, i) => i !== index)
        }));
        
        setSnackbar({
            open: true,
            message: 'Important date removed',
            severity: 'info'
        });
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '';
        
        try {
            // Try to parse the date
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;
            
            // Format as DD/MM (day/month without year)
            const day = date.getDate().toString().padStart(2, '0');
            const month = (date.getMonth() + 1).toString().padStart(2, '0');
            return `${day}/${month}`;
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    // Handle dialog close with proper focus management
    const handleDialogClose = (event, reason) => {
        // Prevent closing during save operation
        if (loading) return;
        
        // Immediately blur active element
        if (document.activeElement && document.activeElement.blur) {
            document.activeElement.blur();
        }
        
        // Call onClose immediately - focus cleanup happens in TransitionProps.onExited
        onClose();
    };

    const handleTranscriptionComplete = (transcribedText) => {
        setEditedContact(prev => ({
            ...prev,
            notes: prev.notes ? `${prev.notes}\n\n${transcribedText}` : transcribedText
        }));
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleDialogClose}
                fullWidth
                fullScreen={isMobile}
                maxWidth="md"
                className="business-card-dialog"
                disableEscapeKeyDown={loading}
                aria-labelledby="edit-contact-dialog-title"
                aria-describedby="edit-contact-dialog-description"
                PaperProps={{
                    sx: {
                        margin: { xs: 0, sm: '32px' },
                        borderRadius: { xs: 0, sm: 1 },
                        height: { xs: '100vh', sm: 'auto' },
                        maxHeight: { xs: '100vh', sm: '90vh' }
                    }
                }}
                TransitionProps={{
                    onExited: () => {
                        // Force clear all focus after dialog closes
                        setTimeout(() => {
                            // Remove all aria-hidden attributes that might be causing issues
                            document.querySelectorAll('[aria-hidden="true"]').forEach(el => {
                                el.removeAttribute('aria-hidden');
                            });
                            
                            // Clear all focused elements
                            document.querySelectorAll(':focus').forEach(el => {
                                if (el.blur) el.blur();
                            });
                            
                            // Focus body as fallback
                            if (document.body) {
                                document.body.focus();
                                document.body.blur();
                            }
                        }, 50);
                    }
                }}
            >
                <DialogTitle id="edit-contact-dialog-title">
                    {t.editContact || "Edit Contact"}
                    <IconButton
                        aria-label="close"
                        onClick={handleDialogClose}
                        disabled={loading}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                
                <DialogContent dividers id="edit-contact-dialog-description">
                    {error && (
                        <Box sx={{ mb: 2 }}>
                            <Typography color="error">{error}</Typography>
                        </Box>
                    )}
                    
                    <Grid container spacing={2}>
                        {/* Basic information */}
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t.fullName || "Full Name"}
                                name="fullName"
                                value={editedContact.fullName || ''}
                                onChange={handleInputChange}
                                margin="normal"
                            />
                        </Grid>
                        
                        {/* Phone & Telegram */}
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone"
                                name="phone"
                                value={editedContact.phone || ''}
                                onChange={handleInputChange}
                                margin="normal"
                                placeholder="+1234567890"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Telegram"
                                value={editedContact.socialMedia?.telegram || ''}
                                onChange={(e) => setEditedContact(prev => ({
                                    ...prev,
                                    socialMedia: { ...prev.socialMedia, telegram: e.target.value }
                                }))}
                                margin="normal"
                                placeholder="@username"
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <RelationshipRhythmSlider
                                value={editedContact.contactFrequency || 30}
                                onChange={(value) => setEditedContact(prev => ({
                                    ...prev,
                                    contactFrequency: value
                                }))}
                            />
                        </Grid>
                        
                        {/* Location information */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ mt: { xs: 2, sm: 1.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                                    <Typography variant="subtitle2">{t.location || "Location"}</Typography>
                                    <Button 
                                        variant="outlined" 
                                        color="primary" 
                                        size="small"
                                        onClick={() => setIsLocationPickerOpen(true)}
                                    >
                                        {editedContact.structuredCity || editedContact.structuredCountry ? t.changeLocation || "Change" : t.selectLocation || "Select"}
                                    </Button>
                                </Box>
                                
                                {(editedContact.structuredCity || editedContact.structuredCountry) ? (
                                    <Box sx={{ 
                                        p: 1.5, 
                                        border: '1px solid', 
                                        borderColor: 'divider', 
                                        borderRadius: 1,
                                        minHeight: 72
                                    }}>
                                        <Typography variant="body2">
                                            {[editedContact.structuredCity, editedContact.structuredCountry].filter(Boolean).join(', ')}
                                        </Typography>
                                        {editedContact.coordinates && (
                                            <Typography variant="caption" color="text.secondary">
                                                {t.coordinatesAvailable || "Coordinates available"}
                                            </Typography>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ 
                                        p: 1.5,
                                        border: '1px dashed',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        minHeight: 72,
                                        display: 'flex',
                                        alignItems: 'center'
                                    }}>
                                        <Typography variant="body2" color="text.secondary">
                                            {t.noLocationSelected || "No location selected"}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={t.address || "Address"}
                                name="address"
                                value={editedContact.address || ''}
                                onChange={handleInputChange}
                                margin="normal"
                            />
                        </Grid>
                        
                        {/* Important Dates */}
                        <Grid item xs={12}>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center', 
                                mb: 1 
                            }}>
                                <Typography variant="subtitle1">
                                    {t.importantDates || "Important Dates"}
                                </Typography>
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={() => setIsImportantDateDialogOpen(true)}
                                    size="small"
                                    variant="outlined"
                                >
                                    {t.addDate || "Add Date"}
                                </Button>
                            </Box>
                            
                            {editedContact.importantDates && editedContact.importantDates.length > 0 ? (
                                <List dense sx={{ 
                                    bgcolor: 'background.paper',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    mb: 2
                                }}>
                                    {editedContact.importantDates.map((date, index) => (
                                        <ListItem key={index}>
                                                <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <EventIcon fontSize="small" sx={{ mr: 1 }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                            {formatDate(date.date)}
                                                        </Typography>
                                                    </Box>
                                                }
                                                secondary={
                                                    <Box sx={{ pl: 3.5 }}>
                                                        <Typography variant="body2">
                                                            {date.notes || ""}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton 
                                                    edge="end" 
                                                    size="small"
                                                    onClick={() => handleRemoveImportantDate(index)}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography 
                                    variant="body2" 
                                    color="text.secondary" 
                                    sx={{ mb: 2, fontStyle: 'italic' }}
                                >
                                    {t.noImportantDates || "No important dates added yet"}
                                </Typography>
                            )}
                        </Grid>
                        
                        {/* Теги */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle1" gutterBottom>
                                {t.tags || "Tags"}
                            </Typography>
                            <Autocomplete
                                multiple
                                freeSolo
                                options={[]}
                                value={editedContact.tags || []}
                                onChange={(event, newValue) => {
                                    setEditedContact(prev => ({
                                        ...prev,
                                        tags: newValue.map(tag => 
                                            typeof tag === 'string' ? tag.trim().toLowerCase() : tag
                                        )
                                    }));
                                }}
                                renderTags={(value, getTagProps) =>
                                    value.map((option, index) => {
                                        const { key, ...chipProps } = getTagProps({ index });
                                        return (
                                            <Chip
                                                key={key}
                                                label={option}
                                                {...chipProps}
                                                size="small"
                                            />
                                        );
                                    })
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t.tags || "Tags"}
                                        placeholder={t.addTags || "Add tags"}
                                        helperText={t.tagsHelper || "Type a tag and press Enter to add it"}
                                        fullWidth
                                        margin="normal"
                                    />
                                )}
                            />
                        </Grid>
                        
                        {/* Примечания */}
                        <Grid item xs={12}>
                            <Box sx={{ mb: 1 }}>
                                <Typography variant="subtitle2" color="primary" gutterBottom>
                                    💡 {t.notes || "Notes"} - {t.notesSubtitle || "Personal insights and relationship details"}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {t.notesHelper || "Add personal insights to strengthen your relationship"}
                                </Typography>
                            </Box>
                            
                            {/* Подсказки для Notes */}
                            <Box sx={{ 
                                mb: 2, 
                                p: 1.5, 
                                bgcolor: 'background.paper', 
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                fontSize: '0.75rem'
                            }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                                    📝 {t.notesSuggestions || "Suggestions for networking notes"}:
                                </Typography>
                                <Box component="ul" sx={{ 
                                    m: 0, 
                                    pl: 2, 
                                    '& li': { 
                                        mb: 0.5, 
                                        fontSize: '0.7rem',
                                        lineHeight: 1.3
                                    }
                                }}>
                                    <li>
                                        <strong>🤝 {t.howWeMet || "How we met"}</strong>
                                    </li>
                                    <li>
                                        <strong>💭 {t.firstImpression || "First impression"}</strong>
                                    </li>
                                    <li>
                                        <strong>🎯 {t.personality || "Personality traits"}</strong>
                                    </li>
                                    <li>
                                        <strong>👨‍👩‍👧‍👦 {t.family || "Family info"}</strong>
                                    </li>
                                    <li>
                                        <strong>🎨 {t.interests || "Interests & hobbies"}</strong>
                                    </li>
                                    <li>
                                        <strong>💼 {t.career || "Career goals"}</strong>
                                    </li>
                                    <li>
                                        <strong>🌍 {t.values || "Values & beliefs"}</strong>
                                    </li>
                                    <li>
                                        <strong>🎁 {t.gifts || "Gift preferences"}</strong>
                                    </li>
                                    <li>
                                        <strong>📅 {t.contactFrequency || "Contact frequency"}</strong>
                                    </li>
                                    <li>
                                        <strong>🔗 {t.networkValue || "Network value"}</strong>
                                    </li>
                                </Box>
                            </Box>
                            
                            <TextField
                                fullWidth
                                label={t.notes || "Notes"}
                                name="notes"
                                multiline
                                rows={6}
                                value={editedContact.notes || ''}
                                onChange={handleInputChange}
                                margin="normal"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ alignSelf: 'center', mr: -0.5 }}>
                                            <CompactVoiceRecorder 
                                                onTranscriptionComplete={handleTranscriptionComplete}
                                                onError={(err) => console.error('Voice recording error:', err)}
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>
                        
                        {/* Connector checkbox */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                                <span style={{ fontSize: '20px' }}>
                                    ⭐️
                                </span>
                                <Typography variant="body2" color="text.secondary">
                                    {t.connector || "Connector"} - {t.connectorDescription || "This contact can introduce you to new interesting people"}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <input
                                    type="checkbox"
                                    id="isConnector"
                                    checked={editedContact.isConnector || false}
                                    onChange={(e) => setEditedContact(prev => ({
                                        ...prev,
                                        isConnector: e.target.checked
                                    }))}
                                    style={{ marginRight: '8px' }}
                                />
                                <label htmlFor="isConnector" style={{ cursor: 'pointer' }}>
                                    <Typography variant="body2">
                                        {t.markAsConnector || "Mark as Connector"}
                                    </Typography>
                                </label>
                            </Box>
                        </Grid>
                        
                        {/* Brain checkbox */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
                                <span style={{ fontSize: '20px' }}>
                                    🧠
                                </span>
                                <Typography variant="body2" color="text.secondary">
                                    {t.brain || "Brain"} - {t.brainDescription || "This contact accumulates knowledge and experience"}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                <input
                                    type="checkbox"
                                    id="isBrain"
                                    checked={editedContact.isBrain || false}
                                    onChange={(e) => setEditedContact(prev => ({
                                        ...prev,
                                        isBrain: e.target.checked
                                    }))}
                                    style={{ marginRight: '8px' }}
                                />
                                <label htmlFor="isBrain" style={{ cursor: 'pointer' }}>
                                    <Typography variant="body2">
                                        {t.markAsBrain || "Mark as Brain"}
                                    </Typography>
                                </label>
                            </Box>
                        </Grid>
                    </Grid>
                </DialogContent>
                
                <DialogActions>
                    <Button 
                        onClick={handleDialogClose}
                        color="inherit"
                    >
                        {t.cancel || "Cancel"}
                    </Button>
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        startIcon={<SaveIcon />}
                        disabled={loading}
                    >
                        {t.save || "Save"}
                    </Button>
                </DialogActions>
                
                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={6000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Dialog>
            
            {/* Location Picker Dialog */}
            <LocationPickerDialog
                open={isLocationPickerOpen}
                onClose={() => setIsLocationPickerOpen(false)}
                onLocationSelect={handleLocationSelect}
                initialLocation={editedContact ? {
                    structuredCity: editedContact.structuredCity || '',
                    structuredCountry: editedContact.structuredCountry || '',
                    coordinates: editedContact.coordinates || null
                } : null}
                t={t}
            />
            
            {/* Important Date Dialog */}
            <AddImportantDateDialog
                open={isImportantDateDialogOpen}
                onClose={() => setIsImportantDateDialogOpen(false)}
                onAdd={handleAddImportantDate}
            />
        </>
    );
};

export default EditContactDialog; 
